import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Security as SecurityIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import {
  getFactorHints,
  startSmsSignInVerification,
  completeSmsSignInVerification,
  completeTotpSignInVerification,
  FACTOR_IDS,
  clearRecaptchaVerifier
} from '../../services/mfaService';

/**
 * MFA Verification Component
 * Displayed during login when MFA is required
 */
const MfaVerification = ({ resolver, onSuccess, onCancel }) => {
  const [verificationCode, setVerificationCode] = useState('');
  const [smsVerificationId, setSmsVerificationId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedFactorIndex, setSelectedFactorIndex] = useState(0);
  const [smsSent, setSmsSent] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  
  const recaptchaContainerRef = useRef(null);
  const factorHints = getFactorHints(resolver);
  
  // Get the selected factor
  const selectedFactor = factorHints[selectedFactorIndex];
  const isPhoneFactor = selectedFactor?.factorId === FACTOR_IDS.PHONE;
  const isTotpFactor = selectedFactor?.factorId === FACTOR_IDS.TOTP;

  // Countdown timer for SMS resend
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearRecaptchaVerifier();
    };
  }, []);

  // Send SMS code when phone factor is selected
  const handleSendSmsCode = async () => {
    if (!isPhoneFactor) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const verificationId = await startSmsSignInVerification(
        resolver,
        selectedFactorIndex,
        'mfa-recaptcha-container'
      );
      setSmsVerificationId(verificationId);
      setSmsSent(true);
      setResendCountdown(60); // 60 second cooldown before resend
    } catch (err) {
      console.error('Error sending SMS:', err);
      
      // Handle specific errors
      if (err.message === 'Timeout' || err.toString().includes('Timeout')) {
        setError('reCAPTCHA timed out. Please try again or contact support if the issue persists.');
      } else if (err.code === 'auth/invalid-app-credential') {
        setError('reCAPTCHA verification failed. Please try again in a few minutes while configuration propagates, or contact your administrator.');
      } else {
        setError(err.message || 'Failed to send verification code');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle verification code submission
  const handleVerify = async (e) => {
    e.preventDefault();
    
    if (!verificationCode || verificationCode.length < 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      let userCredential;
      
      if (isPhoneFactor) {
        if (!smsVerificationId) {
          setError('Please click "Send Code" first to receive your verification code.');
          setLoading(false);
          return;
        }
        userCredential = await completeSmsSignInVerification(
          resolver,
          smsVerificationId,
          verificationCode
        );
      } else if (isTotpFactor) {
        userCredential = await completeTotpSignInVerification(
          resolver,
          selectedFactorIndex,
          verificationCode
        );
      }
      
      if (userCredential) {
        onSuccess(userCredential);
      }
    } catch (err) {
      console.error('MFA verification error:', err);
      if (err.code === 'auth/invalid-verification-code') {
        setError('Invalid verification code. Please try again.');
      } else if (err.code === 'auth/code-expired') {
        setError('Verification code has expired. Please request a new one.');
        setSmsSent(false);
        setSmsVerificationId(null);
      } else {
        setError(err.message || 'Verification failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle factor tab change
  const handleFactorChange = (event, newIndex) => {
    setSelectedFactorIndex(newIndex);
    setVerificationCode('');
    setError(null);
    setSmsSent(false);
    setSmsVerificationId(null);
  };

  // Format phone number for display (mask middle digits)
  const formatPhoneNumber = (phone) => {
    if (!phone) return 'Phone';
    // Show last 4 digits
    return `***-***-${phone.slice(-4)}`;
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f5f5f5',
        p: 2
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 420,
          width: '100%',
          borderRadius: 2
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <SecurityIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
          <Typography variant="h5" component="h1" gutterBottom>
            Two-Factor Authentication
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Enter the verification code to continue
          </Typography>
        </Box>

        {/* Factor selection tabs (if multiple factors enrolled) */}
        {factorHints.length > 1 && (
          <Tabs
            value={selectedFactorIndex}
            onChange={handleFactorChange}
            variant="fullWidth"
            sx={{ mb: 3 }}
          >
            {factorHints.map((factor, index) => (
              <Tab
                key={factor.uid}
                icon={factor.factorId === FACTOR_IDS.PHONE ? <PhoneIcon /> : <SecurityIcon />}
                label={factor.factorId === FACTOR_IDS.PHONE 
                  ? formatPhoneNumber(factor.phoneNumber)
                  : factor.displayName || 'Authenticator'
                }
              />
            ))}
          </Tabs>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleVerify}>
          {/* SMS Factor UI */}
          {isPhoneFactor && (
            <Box sx={{ mb: 2 }}>
              {!smsSent ? (
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    We'll send a verification code to your phone number ending in{' '}
                    <strong>{selectedFactor.phoneNumber?.slice(-4) || '****'}</strong>
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={handleSendSmsCode}
                    disabled={loading}
                    fullWidth
                    startIcon={loading ? <CircularProgress size={20} /> : <PhoneIcon />}
                  >
                    {loading ? 'Sending...' : 'Send Code'}
                  </Button>
                </Box>
              ) : (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                    Enter the 6-digit code sent to your phone
                  </Typography>
                  <TextField
                    fullWidth
                    label="Verification Code"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    inputProps={{ 
                      maxLength: 6,
                      style: { textAlign: 'center', letterSpacing: '0.5em', fontSize: '1.5rem' }
                    }}
                    autoFocus
                    disabled={loading}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                    <Tooltip title={resendCountdown > 0 ? `Wait ${resendCountdown}s` : 'Resend code'}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={handleSendSmsCode}
                          disabled={loading || resendCountdown > 0}
                        >
                          <RefreshIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                      {resendCountdown > 0 ? `Resend in ${resendCountdown}s` : 'Resend code'}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* TOTP Factor UI */}
          {isTotpFactor && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                Enter the 6-digit code from your authenticator app
              </Typography>
              <TextField
                fullWidth
                label="Authenticator Code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                inputProps={{ 
                  maxLength: 6,
                  style: { textAlign: 'center', letterSpacing: '0.5em', fontSize: '1.5rem' }
                }}
                autoFocus
                disabled={loading}
              />
            </Box>
          )}

          {/* Submit button - show for TOTP or when SMS code is sent */}
          {(isTotpFactor || smsSent) && (
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading || verificationCode.length < 6}
              sx={{ mb: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Verify'}
            </Button>
          )}

          <Button
            variant="text"
            fullWidth
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
        </form>

        {/* Hidden reCAPTCHA container */}
        <div id="mfa-recaptcha-container" ref={recaptchaContainerRef} />
      </Paper>
    </Box>
  );
};

export default MfaVerification;

